import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { Button } from '../ui/button';
import { SavedFilter, filterService } from '../../services/filterService';
import { FilterState } from '../../types/filters';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from '../ui/use-toast';
import { SaveFilterDialog } from './SaveFilterDialog';
import { getDateRangeForPreset, formatDateForInput } from '../../utils/dateUtils';

interface SavedFiltersDropdownProps {
  onFilterSelect: (filter: FilterState) => void;
  currentFilter: FilterState;
  className?: string;
  onSaveFilter: () => void;
}

export interface SavedFiltersDropdownRef {
  loadSavedFilters: () => Promise<void>;
  resetSelection: () => void;
}

export const SavedFiltersDropdown = forwardRef<SavedFiltersDropdownRef, SavedFiltersDropdownProps>(
  ({ onFilterSelect, currentFilter, className, onSaveFilter }, ref) => {
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
    const [selectedFilterId, setSelectedFilterId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    
    // Load saved filters
    const loadSavedFilters = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const filters = await filterService.getUserFilters();
        setSavedFilters(filters);
      } catch (err) {
        console.error('Error loading saved filters:', err);
        setError('Failed to load saved filters');
        // Don't show toast for this error since we'll handle it in the UI
      } finally {
        setIsLoading(false);
      }
    };
    
    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      loadSavedFilters,
      resetSelection: () => {
        setSelectedFilterId('');
      }
    }));
    
    // Load filters on mount
    useEffect(() => {
      loadSavedFilters();
    }, []);
    
    // Handle filter selection
    const handleFilterSelect = (filterId: string) => {
      if (!filterId) {
        setSelectedFilterId('');
        return;
      }
      
      const filter = savedFilters.find(f => f.id === filterId);
      if (filter) {
        setSelectedFilterId(filterId);
        
        // If the filter has a datePreset, recalculate the date range
        if (filter.filter.datePreset) {
          const dateRange = getDateRangeForPreset(filter.filter.datePreset);
          
          // Apply the updated date range
          onFilterSelect({
            ...filter.filter,
            dateRange: {
              from: formatDateForInput(dateRange.from),
              to: formatDateForInput(dateRange.to)
            }
          });
        } else {
          // No datePreset, just apply the filter as is
          onFilterSelect(filter.filter);
        }
        
        // Log for debugging
        console.log('Selected filter:', filter.name, 'with ID:', filterId);
      }
    };
    
    // Default filter state
    const defaultFilter: FilterState = {
      dateRange: {
        from: '',
        to: ''
      },
      datePreset: 'all_dates',
      category: 'all',
      enterer: 'all',
      recipient: 'all'
    };
    
    // Handle filter deletion
    const handleDeleteFilter = async (filterId: string, event: React.MouseEvent<HTMLButtonElement>) => {
      // Stop event propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      console.log('Deleting filter:', filterId); // Debug log
      
      try {
        await filterService.deleteFilter(filterId);
        
        // Update the list
        setSavedFilters(prev => prev.filter(f => f.id !== filterId));
        
        // Clear selection if the deleted filter was selected
        if (selectedFilterId === filterId) {
          setSelectedFilterId('');
          
          // Reset all filters to default state
          onFilterSelect(defaultFilter);
          
          console.log('Reset filters to default state');
        }
        
        toast({
          title: 'Filter deleted',
          description: 'Your saved filter has been deleted.',
          variant: 'default'
        });
      } catch (err) {
        console.error('Error deleting filter:', err);
        toast({
          title: 'Error',
          description: 'Failed to delete filter',
          variant: 'destructive'
        });
      }
    };
    
    // Handle filter edit
    const handleEditFilter = (filter: SavedFilter, event: React.MouseEvent<HTMLButtonElement>) => {
      // Stop event propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      console.log('Editing filter:', filter.id); // Debug log
      
      setEditingFilter(filter);
      setIsEditDialogOpen(true);
    };
    
    // Handle edit success
    const handleEditSuccess = () => {
      loadSavedFilters();
      setEditingFilter(null);
      setIsEditDialogOpen(false); // Ensure dialog is closed
    };
    
    return (
      <div className={className}>
        <div>
          <h3 className="text-sm font-medium mb-2 dark:text-white">Saved Filters</h3>
          {error ? (
            <div className="text-sm text-red-500 dark:text-red-400 mb-2">
              {error}
              <Button 
                variant="link" 
                size="sm" 
                className="text-blue-500 dark:text-blue-400 p-0 h-auto ml-2"
                onClick={() => loadSavedFilters()}
              >
                Retry
              </Button>
            </div>
          ) : null}
          <Select
            value={selectedFilterId}
            onValueChange={handleFilterSelect}
            disabled={isLoading || savedFilters.length === 0}
          >
            <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <SelectValue placeholder={
                isLoading 
                  ? 'Loading filters...' 
                  : savedFilters.length === 0 
                    ? 'No saved filters yet' 
                    : 'Select a saved filter'
              } />
            </SelectTrigger>
            <SelectContent>
              {savedFilters.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  No saved filters. Create one by clicking "Save Current..."
                </div>
              ) : (
                <>
                  {/* Standard SelectItems for proper dropdown functionality */}
                  {savedFilters.map(filter => (
                    <SelectItem key={filter.id} value={filter.id}>
                      {filter.name}
                    </SelectItem>
                  ))}
                  
                  {/* Separator */}
                  <div className="h-px bg-gray-200 dark:bg-gray-700 my-2"></div>
                  
                  {/* Filter actions section */}
                  <div className="px-2 py-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Filter Actions</h4>
                    {selectedFilterId && (
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            const filter = savedFilters.find(f => f.id === selectedFilterId);
                            if (filter) {
                              handleEditFilter(filter, e);
                            }
                          }}
                        >
                          <Pencil className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs flex-1 text-red-500 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFilter(selectedFilterId, e);
                          }}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* Edit Filter Dialog */}
        {editingFilter && (
          <SaveFilterDialog
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            currentFilter={currentFilter}
            onSuccess={handleEditSuccess}
            existingFilterId={editingFilter.id}
            existingFilterName={editingFilter.name}
          />
        )}
      </div>
    );
  }
);

// Add display name for better debugging
SavedFiltersDropdown.displayName = 'SavedFiltersDropdown';
